import { useEffect, useRef } from 'react';

export type DrawFn = (ctx: CanvasRenderingContext2D, width: number, height: number, now: number) => void;

/**
 * Drives a <canvas> with requestAnimationFrame at device pixel ratio. The draw
 * callback is read from a ref, so it can change every render without restarting
 * the loop. Drawing pauses while the page is hidden (rAF stops by itself).
 */
export function useCanvas(draw: DrawFn) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    let w = 0;
    let h = 0;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    const loop = (t: number) => {
      drawRef.current(ctx, w, h, t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return ref;
}

export interface Palette {
  track: string;
  ok: string;
  warn: string;
  over: string;
  hold: string;
  scale: string;
  text: string;
  textMuted: string;
  scopeBg: string;
  scopeGrid: string;
  scopeInk: string;
  graphShort: string;
  graphMomentary: string;
  graphBand: string;
  graphTarget: string;
  divider: string;
  font: string;
}

function readPalette(): Palette {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    track: v('--meter-track'),
    ok: v('--meter-ok'),
    warn: v('--meter-warn'),
    over: v('--meter-over'),
    hold: v('--meter-hold'),
    scale: v('--meter-scale'),
    text: v('--text'),
    textMuted: v('--text-muted'),
    scopeBg: v('--scope-bg'),
    scopeGrid: v('--scope-grid'),
    scopeInk: v('--scope-ink'),
    graphShort: v('--graph-short'),
    graphMomentary: v('--graph-momentary'),
    graphBand: v('--graph-band'),
    graphTarget: v('--graph-target'),
    divider: v('--divider'),
    font: v('--font'),
  };
}

let palette: Palette | null = null;
let paletteTheme: string | null = null;

/** Canvas colours from the CSS tokens, re-read when the theme changes. */
export function getPalette(): Palette {
  const theme = document.documentElement.getAttribute('data-theme');
  if (!palette || theme !== paletteTheme) {
    palette = readPalette();
    paletteTheme = theme;
  }
  return palette;
}
